import React, { useEffect, useMemo, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { getDetailOrders, receiveOrder } from '../../services/order.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import {
  Button,
  ConfirmDialog,
  DocumentHeader,
  EmptyState,
  ErrorBanner,
  FieldMessage,
  LinesCard,
  MenuItem,
  NumberField,
  ORDER_STATUS,
  PageFrame,
  PhoneLabel,
  PhoneLine,
  ProductCell,
  ReadValue,
  SaveBar,
  Skeleton,
  TotalsList,
  card,
  dayMonthTime,
  fieldStyle,
  labelStyle,
  money,
  quantity,
  tableStyle,
  td,
  th,
  useLanguage,
} from '../../components/purchasing';
import { parseNumber, priceText, qtyText, totalsOf, unitNameOf, vatRateOf } from '../Order/orderLineRules';
import { orderFacts, useOrderPrint } from '../Order/orderShared';

const RECEIVABLE: number[] = [ORDER_STATUS.Approved, ORDER_STATUS.SentToSupplier, ORDER_STATUS.Acknowledged, ORDER_STATUS.PartiallyReceived];

interface Entry {
  now: string;
  paid: string;
}

const round4 = (value: number) => Math.round(value * 10000) / 10000;

/** One check per line, as the receipt API checks it: never more than is still open, prices not negative. */
const checkEntry = (line: any, entry: Entry | undefined) => {
  const ordered = Number(line?.orderQty) || 0;
  const before = Number(line?.deliveryQty) || 0;
  const open = Math.max(0, round4(ordered - before));
  const price = Number(line?.cost) || 0;
  if (open <= 0) return { done: true, open: 0, before, ordered, n: 0, p: price, counted: false, closes: true, total: 0, nowError: '', nowNote: '', paidError: '', paidWas: null as number | null };
  const n = parseNumber(entry?.now ?? '');
  const paidText = String(entry?.paid ?? '').trim();
  const p = parseNumber(paidText);
  let nowError = '';
  let nowNote = '';
  if (n !== null && Number.isNaN(n)) nowError = 'Enter a number';
  else if (n !== null && n < 0) nowError = "Can't be negative";
  else if (n !== null && n > open) nowError = 'Max {qty}';
  else if (n !== null && n > 0 && n < open) nowNote = '{qty} short';
  const counted = !nowError && n !== null && n > 0;
  let paidError = '';
  if (paidText === '' && counted) paidError = 'Enter a unit price';
  else if (p !== null && Number.isNaN(p)) paidError = 'Enter a number';
  else if (p !== null && p < 0) paidError = "Can't be negative";
  const paidWas = !paidError && p !== null && Math.abs(p - price) > 0.0001 ? price : null;
  const total = counted && !paidError && p !== null ? n * p : 0;
  return { done: false, open, before, ordered, n: n ?? 0, p: p ?? price, counted, closes: !nowError && n === open, total, nowError, nowNote, paidError, paidWas };
};

/** Receiving a sent order: count what arrived, check the prices, save one receipt; stock follows each receipt. */
const DeliveriesDetails = () => {
  const preLanguage = localStorage.getItem('language');
  const id: string = window.location.pathname.split('/').pop() || '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [deliveryNo, setDeliveryNo] = useState('');
  const [note, setNote] = useState('');
  const [query, setQuery] = useState('');
  const [flagged, setFlagged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<'' | 'close' | 'leave'>('');

  const lines: any[] = order?.orderDetails ?? [];
  const statusId = Number(order?.statusId);
  const receivable = RECEIVABLE.includes(statusId);
  const title = order ? t('Order #{number}', { number: order.internalNumber }) : t('Order');
  const site = order?.location?.name ?? '';

  const baseline = (raw: any): Record<string, Entry> =>
    Object.fromEntries((raw?.orderDetails ?? []).map((line: any) => [line.id, { now: '', paid: priceText(line.cost) }]));

  const apply = (raw: any) => {
    setOrder(raw);
    setEntries(baseline(raw));
    setDeliveryNo('');
    setNote('');
    setFlagged(false);
  };

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const response: any = await getDetailOrders(id);
      apply(response?.data?.data);
    } catch (error: any) {
      setFailed(true);
      showApiError(error, { language });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id === 'new') {
      webNavigate(`/${preLanguage}/orderManagement`);
      return;
    }
    load();
    const onToken = () => load();
    document.addEventListener('newToken', onToken);
    return () => document.removeEventListener('newToken', onToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const checks = useMemo(() => new Map(lines.map((line) => [line.id, checkEntry(line, entries[line.id])])), [lines, entries]);
  const open = lines.filter((line) => !checks.get(line.id)?.done);
  const counted = open.filter((line) => checks.get(line.id)?.counted);
  const attentionLines = open.filter((line) => {
    const check = checks.get(line.id);
    return !!(check?.nowError || check?.paidError);
  });
  const shortCount = open.filter((line) => checks.get(line.id)?.nowNote).length;
  const priceChanges = open.filter((line) => checks.get(line.id)?.paidWas !== null).length;
  const dirty = counted.length > 0 || attentionLines.length > 0 || priceChanges > 0 || deliveryNo.trim() !== '' || note.trim() !== '';
  const deliveredBefore = lines.some((line) => Number(line.deliveryQty) > 0);
  const stillOpen = lines.filter((line) => !checks.get(line.id)?.closes);
  const totals = totalsOf(open.map((line) => ({ amount: checks.get(line.id)?.total ?? 0, rate: vatRateOf(line) })));

  const term = query.trim().toLowerCase();
  const shown = term ? lines.filter((line) => `${line.product?.productName ?? ''} ${line.product?.sku ?? ''}`.toLowerCase().includes(term)) : lines;

  const setEntry = (lineId: string, change: Partial<Entry>) => setEntries((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...change } }));
  const receiveAll = () =>
    setEntries((prev) => {
      const next = { ...prev };
      lines.forEach((line) => {
        const check = checks.get(line.id);
        if (check && !check.done) next[line.id] = { ...next[line.id], now: qtyText(check.open) };
      });
      return next;
    });
  const discard = () => {
    setEntries(baseline(order));
    setDeliveryNo('');
    setNote('');
    setFlagged(false);
  };

  // Keeps the order page's way back to the Deliveries list.
  const orderPath = `/${preLanguage}/orders/${id}${new URLSearchParams(location.search).get('from') === 'deliveries' ? '?from=deliveries' : ''}`;
  const onBack = () => (dirty ? setConfirm('leave') : webNavigate(orderPath));

  const submit = async (close: boolean) => {
    if (busy) return;
    if (attentionLines.length > 0 || (!close && counted.length === 0)) {
      setFlagged(true);
      return;
    }
    setBusy(true);
    try {
      const response: any = await receiveOrder(id, {
        lines: counted.map((line) => {
          const check = checks.get(line.id)!;
          return { orderDetailId: line.id, quantity: check.n, unitPrice: check.p };
        }),
        deliveryNoteNo: deliveryNo.trim() || undefined,
        note: note.trim() || undefined,
        close: close || undefined,
      });
      const next = response?.data?.data;
      setConfirm('');
      if (Number(next?.statusId) === ORDER_STATUS.Received) {
        showToast({ message: t('Order {number} is received. Stock is updated at {site}.', { number: `#${order?.internalNumber}`, site }), type: 'success', duration: 6000 });
        webNavigate(orderPath);
      } else {
        showToast({ message: t('Receipt saved. Stock is updated at {site}.', { site }), type: 'success', duration: 6000 });
        if (next) apply(next);
        else await load();
      }
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy(false);
    }
  };

  const printDoc = useOrderPrint(
    order,
    lines.map((line) => ({ sku: line.product?.sku, productName: line.product?.productName, uomName: unitNameOf(line), orderQty: Number(line.orderQty) || 0, cost: Number(line.cost) || 0, lineCost: (Number(line.cost) || 0) * (Number(line.orderQty) || 0) })),
  );

  const moreItems: MenuItem[] = [
    ...(phone && dirty ? [{ key: 'discard', label: t('Discard changes'), icon: 'undo', onSelect: discard }] : []),
    ...(receivable && (deliveredBefore || counted.length > 0)
      ? [{ key: 'close', label: t('Close the order short'), desc: t('Nothing more is coming from this order'), icon: 'check', onSelect: () => setConfirm('close') }]
      : []),
  ];

  /* ---------------- Lines ---------------- */

  const message = (key: string, qty: number) => t(key, { qty: quantity(qty) });
  const nowMessage = (check: any) =>
    check.nowError ? { tone: 'error' as const, text: message(check.nowError, check.open) } : check.nowNote ? { tone: 'notice' as const, text: message(check.nowNote, check.open - check.n) } : null;
  const paidMessage = (check: any) =>
    check.paidError
      ? { tone: 'error' as const, text: t(check.paidError) }
      : check.paidWas !== null
        ? { tone: 'notice' as const, text: t('Was {price}', { price: money(check.paidWas) }) }
        : null;
  const context = (check: any) => (check.done ? t('All received') : t('{qty} still open', { qty: quantity(check.open) }));

  const desktopTable = () => (
    <div data-lines="" style={{ overflowX: 'auto' }}>
      <table style={tableStyle(860)}>
        <thead>
          <tr>
            <th scope="col" style={th('left', { first: true })}>
              {t('Product')}
            </th>
            <th scope="col" style={th('left', { width: 104 })}>
              {t('Order unit')}
            </th>
            <th scope="col" style={th('right', { width: 88 })}>
              {t('Ordered')}
            </th>
            <th scope="col" style={th('right', { width: 116 })}>
              {t('Received before')}
            </th>
            <th scope="col" style={th('right', { width: 104 })}>
              {t('Now')}
            </th>
            <th scope="col" style={th('right', { width: 112 })}>
              {t('Unit price')}
            </th>
            <th scope="col" style={th('right', { width: 110, last: true })}>
              {t('Total')}
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((line) => {
            const check: any = checks.get(line.id);
            const entry = entries[line.id] ?? { now: '', paid: '' };
            const name = line.product?.productName ?? '';
            const error = !!(check.nowError || check.paidError);
            const nowMsg = nowMessage(check);
            const paidMsg = paidMessage(check);
            return (
              <tr key={line.id} className="pu-row">
                <td style={td({ first: true, error })}>
                  <ProductCell name={name} sku={line.product?.sku} context={context(check)} muted={check.done} />
                </td>
                <td style={td({ error })}>
                  <ReadValue muted={check.done}>{unitNameOf(line) || '—'}</ReadValue>
                </td>
                <td style={td({ align: 'right', error })}>
                  <ReadValue muted={check.done}>{quantity(check.ordered)}</ReadValue>
                </td>
                <td style={td({ align: 'right', error })}>
                  <ReadValue muted={check.done || check.before === 0}>{quantity(check.before)}</ReadValue>
                </td>
                <td style={td({ align: 'right', error })}>
                  {check.done ? (
                    <ReadValue muted>{t('Received')}</ReadValue>
                  ) : (
                    <>
                      <NumberField
                        id={`now-${line.id}`}
                        column="now"
                        srLabel={t('Received now of {product}', { product: name })}
                        value={entry.now}
                        invalid={!!check.nowError}
                        describedBy={nowMsg ? `now-${line.id}-msg` : undefined}
                        onChange={(value) => setEntry(line.id, { now: value })}
                      />
                      {nowMsg && (
                        <FieldMessage id={`now-${line.id}-msg`} tone={nowMsg.tone}>
                          {nowMsg.text}
                        </FieldMessage>
                      )}
                    </>
                  )}
                </td>
                <td style={td({ align: 'right', error })}>
                  {check.done ? (
                    <ReadValue muted>{money(line.cost)}</ReadValue>
                  ) : (
                    <>
                      <NumberField
                        id={`paid-${line.id}`}
                        column="paid"
                        prefix="€"
                        srLabel={t('Unit price for {product}', { product: name })}
                        value={entry.paid}
                        invalid={!!paidMsg && paidMsg.tone === 'error'}
                        describedBy={paidMsg ? `paid-${line.id}-msg` : undefined}
                        onChange={(value) => setEntry(line.id, { paid: value })}
                        onBlur={() => {
                          const n = parseNumber(entry.paid);
                          if (n !== null && Number.isFinite(n)) setEntry(line.id, { paid: priceText(n) });
                        }}
                      />
                      {paidMsg && (
                        <FieldMessage id={`paid-${line.id}-msg`} tone={paidMsg.tone}>
                          {paidMsg.text}
                        </FieldMessage>
                      )}
                    </>
                  )}
                </td>
                <td style={td({ align: 'right', last: true, error })}>
                  <ReadValue strong={check.total > 0} muted={!check.total}>
                    {check.total > 0 ? money(check.total) : '—'}
                  </ReadValue>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const phoneLines = () => (
    <div data-lines="">
      {shown.map((line) => {
        const check: any = checks.get(line.id);
        const entry = entries[line.id] ?? { now: '', paid: '' };
        const name = line.product?.productName ?? '';
        const nameId = `rl-${line.id}`;
        const nowMsg = nowMessage(check);
        const paidMsg = paidMessage(check);
        return (
          <PhoneLine key={line.id} labelledBy={nameId} error={!!(check.nowError || check.paidError)}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <ProductCell phone nameId={nameId} name={name} sku={line.product?.sku} context={context(check)} muted={check.done} />
              <p style={{ flex: 'none', fontSize: '15px', lineHeight: '22px', color: '#222525' }}>
                <span className="pu-sr">{t('Order unit')}: </span>
                {unitNameOf(line)}
              </p>
            </div>
            {!check.done && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '10px' }}>
                <div style={{ minWidth: 0 }}>
                  <PhoneLabel htmlFor={`m-now-${line.id}`}>{t('Now')}</PhoneLabel>
                  <NumberField
                    id={`m-now-${line.id}`}
                    column="now"
                    phone
                    value={entry.now}
                    invalid={!!check.nowError}
                    describedBy={nowMsg ? `m-now-${line.id}-msg` : undefined}
                    onChange={(value) => setEntry(line.id, { now: value })}
                  />
                  {nowMsg && (
                    <FieldMessage id={`m-now-${line.id}-msg`} tone={nowMsg.tone}>
                      {nowMsg.text}
                    </FieldMessage>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <PhoneLabel htmlFor={`m-paid-${line.id}`}>{t('Unit price')}</PhoneLabel>
                  <NumberField
                    id={`m-paid-${line.id}`}
                    column="paid"
                    phone
                    prefix="€"
                    value={entry.paid}
                    invalid={!!paidMsg && paidMsg.tone === 'error'}
                    describedBy={paidMsg ? `m-paid-${line.id}-msg` : undefined}
                    onChange={(value) => setEntry(line.id, { paid: value })}
                    onBlur={() => {
                      const n = parseNumber(entry.paid);
                      if (n !== null && Number.isFinite(n)) setEntry(line.id, { paid: priceText(n) });
                    }}
                  />
                  {paidMsg && (
                    <FieldMessage id={`m-paid-${line.id}-msg`} tone={paidMsg.tone}>
                      {paidMsg.text}
                    </FieldMessage>
                  )}
                </div>
                <div style={{ minWidth: 0, textAlign: 'right' }}>
                  <PhoneLabel align="right">{t('Total')}</PhoneLabel>
                  <ReadValue phone strong={check.total > 0} muted={!check.total}>
                    {check.total > 0 ? money(check.total) : '—'}
                  </ReadValue>
                </div>
              </div>
            )}
          </PhoneLine>
        );
      })}
    </div>
  );

  const linesBody = () => {
    if (loading && !order) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: phone ? '16px' : '20px' }}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      );
    }
    if (shown.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          {lines.length === 0 ? (
            <EmptyState phone={phone} title={t('No products on this order')} />
          ) : (
            <EmptyState
              icon="search"
              phone={phone}
              title={t('No products on this order match "{search}". Check the spelling or search by SKU.', { search: query.trim() })}
              action={
                <Button variant="text" phone={phone} onClick={() => setQuery('')}>
                  {t('Clear search')}
                </Button>
              }
            />
          )}
        </div>
      );
    }
    return phone ? phoneLines() : desktopTable();
  };

  const openSummary = (
    <p style={{ fontSize: '14px', lineHeight: '20px', color: '#4A5353', fontVariantNumeric: 'tabular-nums' }}>
      {t('Still open after this receipt:')}{' '}
      <span style={{ fontWeight: 600, color: '#05292A' }}>{stillOpen.length === 0 ? t('none') : stillOpen.length === 1 ? t('1 line') : t('{count} lines', { count: stillOpen.length })}</span>
      {stillOpen.length > 0 && ` · ${stillOpen.map((line) => line.product?.productName ?? '').join(', ')}`}
    </p>
  );

  /* ---------------- Panels ---------------- */

  const panelTitle = (text: string, sub: string, idText: string) => (
    <div>
      <h2 id={idText} style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: '#05292A' }}>
        {text}
      </h2>
      <p style={{ marginTop: '2px', fontSize: '12.5px', lineHeight: '18px', color: '#4A5353' }}>{sub}</p>
    </div>
  );
  const notePanel = (
    <section aria-labelledby="pu-note-title" style={{ ...card, display: 'flex', flexDirection: 'column', gap: '16px', padding: phone ? '16px' : '20px' }}>
      {panelTitle(t('Delivery note'), t('Saved with this receipt'), 'pu-note-title')}
      <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={labelStyle}>{t('Supplier delivery note no.')}</span>
        <input className="pu-input" type="text" maxLength={100} autoComplete="off" value={deliveryNo} onChange={(event) => setDeliveryNo(event.target.value)} style={fieldStyle(phone)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={labelStyle}>{t('Note')}</span>
        <textarea
          className="pu-input"
          rows={3}
          maxLength={1000}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          style={{ ...fieldStyle(phone), height: 'auto', minHeight: '88px', padding: '10px 12px', resize: 'vertical' }}
        />
      </label>
    </section>
  );
  const receipts: any[] = order?.receipts ?? [];
  const receiptsPanel = (
    <section aria-labelledby="pu-receipts-title" style={{ ...card, display: 'flex', flexDirection: 'column', gap: '16px', padding: phone ? '16px' : '20px' }}>
      {panelTitle(t('Receipts on this order'), site ? t('Already added to stock at {site}', { site }) : t('Already added to stock'), 'pu-receipts-title')}
      {receipts.length === 0 ? (
        <p style={{ fontSize: '13px', lineHeight: '18px', color: '#4A5353' }}>{t('No receipts yet. This is the first delivery.')}</p>
      ) : (
        <ul>
          {receipts.map((receipt, index) => (
            <li key={receipt.id} style={{ padding: index === 0 ? '0 0 12px' : '12px 0', borderBottom: index === receipts.length - 1 ? 0 : '1px solid #EBF0F0' }}>
              <p style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: '#222525', fontVariantNumeric: 'tabular-nums' }}>{dayMonthTime(receipt.receivedAt)}</p>
              <p style={{ fontSize: '12.5px', lineHeight: '18px', color: '#4A5353', fontVariantNumeric: 'tabular-nums' }}>
                {[
                  receipt.receivedBy,
                  receipt.lines === 1 ? t('1 line') : t('{count} lines', { count: receipt.lines }),
                  money(receipt.total),
                  receipt.deliveryNoteNo ? t('Note {number}', { number: receipt.deliveryNoteNo }) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  /* ---------------- Save bar ---------------- */

  const detail = [
    counted.length > 0 && totals.total > 0 ? money(totals.total) : null,
    shortCount > 0 ? t('{count} short', { count: shortCount }) : null,
    priceChanges > 0 ? (priceChanges === 1 ? t('1 price change') : t('{count} price changes', { count: priceChanges })) : null,
  ].filter(Boolean) as string[];
  const summary = {
    lead: counted.length > 0 ? (counted.length === 1 ? t('Receiving 1 line') : t('Receiving {count} lines', { count: counted.length })) : dirty ? t('Nothing to receive yet') : t('No unsaved changes'),
    detail,
    attention:
      attentionLines.length > 0
        ? attentionLines.length === 1
          ? t('1 line needs attention')
          : t('{count} lines need attention', { count: attentionLines.length })
        : flagged && counted.length === 0
          ? t('Count at least one line')
          : null,
    quiet: !dirty,
  };
  const saveBar = receivable && (
    <SaveBar
      phone={phone}
      label={t('Receipt')}
      summary={summary}
      secondary={
        !phone ? (
          <Button variant="text" disabled={!dirty || busy} onClick={discard}>
            {t('Discard changes')}
          </Button>
        ) : undefined
      }
      primary={
        <Button variant="primary" phone={phone} fullWidth={phone} disabled={busy || counted.length === 0 || attentionLines.length > 0} aria-busy={busy || undefined} onClick={() => submit(false)}>
          {busy ? t('Saving…') : t('Save receipt')}
        </Button>
      }
    />
  );

  return (
    <>
      {printDoc.node}
      <PageFrame phone={phone} withPhoneBar={receivable}>
        {failed && !order ? (
          <ErrorBanner message={t('The order could not be loaded.')} retryLabel={t('Try again')} onRetry={load} />
        ) : (
          <>
            {order ? (
              <DocumentHeader
                phone={phone}
                t={t}
                backLabel={t('Back to order')}
                onBack={onBack}
                title={title}
                statusId={statusId}
                nextStep={receivable ? { tone: 'progress', text: t('Count what arrived and save the receipt. Lines you leave at 0 stay open on the order.') } : null}
                facts={orderFacts(order, t)}
                onPrint={printDoc.print}
                onDownload={printDoc.download}
                moreItems={moreItems}
              />
            ) : (
              <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: '16px', padding: phone ? '16px' : '20px' }}>
                <Skeleton width="120px" />
                <Skeleton width="220px" height={28} />
                <Skeleton height={96} />
              </section>
            )}
            {order && !receivable ? (
              <EmptyState
                phone={phone}
                title={
                  statusId === ORDER_STATUS.Received
                    ? t('This order is received')
                    : statusId === ORDER_STATUS.Cancelled
                      ? t('This order was cancelled. Nothing was received.')
                      : t('Only an order sent to the supplier can be received')
                }
                action={
                  <Button variant="primary" phone={phone} onClick={() => webNavigate(orderPath)}>
                    {t('Open the order')}
                  </Button>
                }
              />
            ) : (
              <>
                <LinesCard
                  phone={phone}
                  titleId="pu-receive-lines"
                  title={t('Products')}
                  count={order ? lines.length : undefined}
                  search={lines.length > 0 ? { label: t('Find a product in this order'), placeholder: t('Find a product in this order'), value: query, onChange: setQuery } : undefined}
                  actions={
                    open.length > 0 ? (
                      <Button phone={phone} icon="check" disabled={open.every((line) => checks.get(line.id)?.closes)} onClick={receiveAll}>
                        {t('Receive all')}
                      </Button>
                    ) : undefined
                  }
                  footer={
                    lines.length > 0 && (
                      <>
                        {phone && <div style={{ padding: '14px 16px 0' }}>{openSummary}</div>}
                        <TotalsList
                          phone={phone}
                          aside={phone ? undefined : openSummary}
                          rows={[
                            { label: t('Subtotal'), value: money(totals.subtotal) },
                            { label: totals.rate !== null ? t('VAT {rate}%', { rate: quantity(totals.rate) }) : t('VAT'), value: money(totals.vat) },
                            { label: t('Total'), value: money(totals.total), strong: true },
                          ]}
                        />
                      </>
                    )
                  }
                >
                  {linesBody()}
                </LinesCard>
                <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))', gap: phone ? '20px' : '24px', alignItems: 'start' }}>
                  {notePanel}
                  {receiptsPanel}
                </div>
                {saveBar}
              </>
            )}
          </>
        )}
      </PageFrame>
      <ConfirmDialog
        open={confirm === 'close'}
        phone={phone}
        busy={busy}
        title={t('Close order {number} short?', { number: `#${order?.internalNumber ?? ''}` })}
        body={
          counted.length > 0
            ? t('This receipt is saved, the lines still open stay unreceived and the order is marked received. The purchase is booked with what arrived.')
            : t('The lines still open stay unreceived and the order is marked received. The purchase is booked with what arrived.')
        }
        confirmLabel={counted.length > 0 ? t('Save and close the order') : t('Close the order')}
        cancelLabel={t('Cancel')}
        closeLabel={t('Close')}
        onConfirm={() => submit(true)}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'leave'}
        phone={phone}
        danger
        title={t('Leave without saving?')}
        body={t('What you counted for this receipt is not saved.')}
        confirmLabel={t('Leave without saving')}
        cancelLabel={t('Keep counting')}
        closeLabel={t('Close')}
        onConfirm={() => {
          setConfirm('');
          webNavigate(orderPath);
        }}
        onCancel={() => setConfirm('')}
      />
    </>
  );
};

export default DeliveriesDetails;
